import { useState, useEffect, useRef, useMemo, memo, useCallback } from "react";
import { motion } from "framer-motion";
import { FaSolarPanel } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const createObserver = (callback) => {
  return new IntersectionObserver(callback, { threshold: 0.1 });
};

const useCountUp = (end, duration) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const countRef = useRef(null);
  const animationRef = useRef(null);

  const observerCallback = useCallback(
    ([entry]) => {
      if (entry.isIntersecting && !hasAnimated) {
        setIsVisible(true);
        setHasAnimated(true);
      }
    },
    [hasAnimated]
  );

  useEffect(() => {
    const observer = createObserver(observerCallback);

    if (countRef.current) {
      observer.observe(countRef.current);
    }
    return () => observer.disconnect();
  }, [observerCallback]);

  const animate = useCallback(
    (timestamp) => {
      if (!animationRef.current.startTime)
        animationRef.current.startTime = timestamp;
      const elapsedTime = timestamp - animationRef.current.startTime;
      const totalDuration = duration * 700;
      const progress = Math.min(elapsedTime / totalDuration, 1);
      const currentValue = Math.floor(progress * end);

      setCount(currentValue);

      if (progress < 1) {
        animationRef.current.frameId = requestAnimationFrame(animate);
      }
    },
    [duration, end]
  );

  useEffect(() => {
    if (!isVisible) return;

    animationRef.current = {
      startTime: null,
      frameId: null,
    };

    animationRef.current.frameId = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current && animationRef.current.frameId) {
        cancelAnimationFrame(animationRef.current.frameId);
      }
    };
  }, [isVisible, animate]);

  return [count, countRef];
};

const StatCard = memo(({ label, value, duration = 2, suffix = "" }) => {
  const [count, countRef] = useCountUp(value, duration);

  return (
    <div
      ref={countRef}
      className="text-center shadow-lg bg-gray-800/90 p-3 sm:p-4 rounded-lg w-32 sm:w-40"
    >
      <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-400">
        {count.toLocaleString()}
        {suffix}
      </div>
      <div className="text-xs sm:text-sm md:text-md text-gray-400 uppercase">
        {label}
      </div>
    </div>
  );
});

StatCard.displayName = "StatCard";

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  duration: PropTypes.number,
  suffix: PropTypes.string,
};

function Hero({ heroImage }) {
  const { t } = useTranslation();

  const heroData = useMemo(
    () => t("components.aboutUs.hero", { returnObjects: true }),
    [t]
  );

  const stats = useMemo(() => heroData.stats, [heroData]);

  return (
    <div className="relative h-[85vh] landscape:max-lg:h-[125vh] w-full overflow-hidden bg-black text-white">
      <motion.div
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0 overflow-hidden"
      >
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "scroll",
            filter: "brightness(0.6)",
          }}
        />
        <div className="absolute inset-0 bg-linear-to-br from-black via-gray-900/80 to-blue-900 opacity-85" />
      </motion.div>

      <div className="relative h-full max-w-7xl mx-auto px-6 flex items-center justify-center pt-12 overflow-hidden">
        <div className="text-center space-y-6 sm:space-y-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-3 bg-gray-800/90 px-4 py-2 sm:px-5 sm:py-3 rounded-full shadow-md"
          >
            <FaSolarPanel className="text-blue-400 text-lg sm:text-xl" />
            <span className="font-bold text-gray-200 tracking-wider text-sm sm:text-base">
              {heroData.badge}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-transparent bg-clip-text bg-linear-to-r from-blue-400 to-cyan-500"
          >
            {heroData.heading}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-md sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto tracking-wide leading-relaxed"
          >
            {heroData.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-wrap justify-center gap-6 sm:gap-8 overflow-hidden"
          >
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                label={stat.label}
                value={stat.value}
                suffix={stat.suffix}
              />
            ))}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-16 sm:h-24 bg-linear-to-t from-gray-900 to-transparent" />
    </div>
  );
}

Hero.propTypes = {
  heroImage: PropTypes.string.isRequired,
};

export default memo(Hero);
